import React, { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  Typography,
  Grid,
  Box,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  TextField,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Person as PersonIcon,
  CheckCircle as PresentIcon,
  Cancel as AbsentIcon,
  Schedule as LateIcon,
  LocalHospital as SickIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';

interface StudentAttendanceRecord {
  student__student_id: string;
  student__first_name: string;
  student__last_name: string;
  student__gender: string;
  student__age: number;
  class_name: string;
  status: string;
  arrival_time: string | null;
  reason: string | null;
  school__name: string;
}

interface StudentAttendanceData {
  date: string;
  total_students: number;
  status_summary: Record<string, number>;
  attendance_records: StudentAttendanceRecord[];
}

const StudentAttendanceDetails: React.FC = () => {
  const [attendanceData, setAttendanceData] = useState<StudentAttendanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(new Date());
  const [selectedSchool, setSelectedSchool] = useState<string>('');
  const [selectedClass, setSelectedClass] = useState<string>('');
  const [schools, setSchools] = useState<Array<{ id: number; name: string }>>([]);
  const [classes, setClasses] = useState<string[]>([]);

  useEffect(() => {
    fetchFilterOptions();
  }, []);

  useEffect(() => {
    fetchStudentAttendance();
  }, [selectedDate, selectedSchool, selectedClass]);

  const fetchFilterOptions = async () => {
    try {
      const { attendanceAPI } = await import('../../services/attendanceApi');
      const response = await attendanceAPI.getFilterOptions();
      if (response.data.status === 'success') {
        setSchools(response.data.data.schools || []);
        setClasses(response.data.data.classes || []);
      }
    } catch (err) {
      console.error('Failed to load filter options:', err);
    }
  };

  const fetchStudentAttendance = async () => {
    try {
      setLoading(true);
      setError(null);

      const { attendanceAPI } = await import('../../services/attendanceApi');

      const response = await attendanceAPI.getStudentAttendance({
        start_date: selectedDate.toISOString().split('T')[0],
        end_date: selectedDate.toISOString().split('T')[0],
        school: selectedSchool || undefined,
        class: selectedClass || undefined,
      });

      if (response.data.status === 'success') {
        setAttendanceData(response.data.data);
      } else {
        throw new Error(response.data.message || 'Failed to load student attendance data');
      }
    } catch (err: any) {
      console.error('Student attendance error:', err);
      setError(err.response?.data?.message || err.message || 'Failed to load student attendance data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status.toLowerCase()) {
      case 'present':
        return <PresentIcon color="success" />;
      case 'absent':
        return <AbsentIcon color="error" />;
      case 'late':
        return <LateIcon color="warning" />;
      case 'sick':
        return <SickIcon color="info" />;
      default:
        return <PersonIcon />;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'info' | 'default' => {
    switch (status.toLowerCase()) {
      case 'present':
        return 'success';
      case 'absent':
        return 'error';
      case 'late':
        return 'warning';
      case 'sick':
      case 'excused':
        return 'info';
      default:
        return 'default';
    }
  };

  const calculateAttendanceRate = () => {
    if (!attendanceData || attendanceData.total_students === 0) return 0;
    const presentCount = attendanceData.status_summary.present || 0;
    return (presentCount / attendanceData.total_students * 100).toFixed(1);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Student Attendance Details
      </Typography>

      {/* Filters */}
      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Grid container spacing={3} alignItems="center">
            <Grid item xs={12} md={3}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Select Date"
                  value={selectedDate}
                  onChange={(newValue) => newValue && setSelectedDate(newValue)}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      size: 'small'
                    }
                  }}
                />
              </LocalizationProvider>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>School</InputLabel>
                <Select
                  value={selectedSchool}
                  label="School"
                  onChange={(e) => setSelectedSchool(e.target.value)}
                >
                  <MenuItem value="">All Schools</MenuItem>
                  {schools.map((school) => (
                    <MenuItem key={school.id} value={school.id.toString()}>
                      {school.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth size="small">
                <InputLabel>Class</InputLabel>
                <Select
                  value={selectedClass}
                  label="Class"
                  onChange={(e) => setSelectedClass(e.target.value)}
                >
                  <MenuItem value="">All Classes</MenuItem>
                  {classes.map((cls) => (
                    <MenuItem key={cls} value={cls}>
                      {cls}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <Button
                variant="contained"
                onClick={fetchStudentAttendance}
                fullWidth
              >
                Refresh
              </Button>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {attendanceData && (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Overall Attendance
                  </Typography>
                  <Typography variant="h3" color="primary">
                    {calculateAttendanceRate()}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    {attendanceData.status_summary.present || 0} of {attendanceData.total_students} present
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <PresentIcon color="success" sx={{ mr: 1 }} />
                    <Typography variant="h6">Present</Typography>
                  </Box>
                  <Typography variant="h3" color="success.main">
                    {attendanceData.status_summary.present || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <AbsentIcon color="error" sx={{ mr: 1 }} />
                    <Typography variant="h6">Absent</Typography>
                  </Box>
                  <Typography variant="h3" color="error.main">
                    {attendanceData.status_summary.absent || 0}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>

            <Grid item xs={12} md={3}>
              <Card>
                <CardContent>
                  <Box display="flex" alignItems="center" mb={1}>
                    <LateIcon color="warning" sx={{ mr: 1 }} />
                    <Typography variant="h6">Late/Other</Typography>
                  </Box>
                  <Typography variant="h3" color="warning.main">
                    {(attendanceData.status_summary.late || 0) +
                      (attendanceData.status_summary.excused || 0) +
                      (attendanceData.status_summary.sick || 0)}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Attendance Records Table */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Student Attendance Records - {attendanceData.date}
              </Typography>

              {attendanceData.attendance_records.length > 0 ? (
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Student ID</TableCell>
                        <TableCell>Student Name</TableCell>
                        <TableCell>Gender</TableCell>
                        <TableCell>Age</TableCell>
                        <TableCell>Class</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Arrival Time</TableCell>
                        <TableCell>Reason</TableCell>
                        <TableCell>School</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {attendanceData.attendance_records.map((record, index) => (
                        <TableRow key={index} hover>
                          <TableCell>{record.student__student_id}</TableCell>
                          <TableCell>
                            <Box display="flex" alignItems="center">
                              {getStatusIcon(record.status)}
                              <Box ml={1}>
                                {record.student__first_name} {record.student__last_name}
                              </Box>
                            </Box>
                          </TableCell>
                          <TableCell>{record.student__gender}</TableCell>
                          <TableCell>{record.student__age}</TableCell>
                          <TableCell>{record.class_name}</TableCell>
                          <TableCell>
                            <Chip
                              label={record.status}
                              color={getStatusColor(record.status)}
                              size="small"
                              sx={{ textTransform: 'capitalize' }}
                            />
                          </TableCell>
                          <TableCell>
                            {record.arrival_time || '-'}
                          </TableCell>
                          <TableCell>{record.reason || '-'}</TableCell>
                          <TableCell>{record.school__name}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              ) : (
                <Alert severity="info">
                  No attendance records found for the selected date and filters.
                </Alert>
              )}
            </CardContent>
          </Card>
        </>
      )}
    </Box>
  );
};

export default StudentAttendanceDetails;